import { Box } from '@wix/design-system';
import { convertElementStatusToBooleanStatus, useAutoScrolling, useDeepCompare, useElementToggle } from '@wix/rules-panel-ui';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';
import { currentElementInfoAtom } from '../../../atoms/bi-atoms';
import { scrollerNodeRefAtom } from '../../../atoms/session-atoms';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
import { convertBooleanStatusToElementStatus } from '../alt-image-fixer/utils';
import { useInaccessibleLayoutAnalytics } from './hooks/use-inaccessible-layout-analytics';
import { useSyncSessionLayout } from './hooks/use-sync-session-layout';
import { InaccessibleLayoutRule } from './inaccessible-layout-rule';
export const InaccessibleLayoutRuleFixer = _ref => {
  let {
    failedElements,
    pageId,
    elements
  } = _ref;
  const setCurrentElementInfo = useSetAtom(currentElementInfoAtom);
  const {
    openNextElement,
    toggleElement,
    selectedElementId
  } = useElementToggle();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const scrollerNodeRef = useAtomValue(scrollerNodeRefAtom);
  const {
    selectedItemContentRef,
    selectedItemHeaderRef
  } = useAutoScrolling(selectedElementId, scrollerNodeRef);
  useSyncSessionLayout(failedElements, pageId);
  const {
    manualTaskDataEvent
  } = useInaccessibleLayoutAnalytics({
    elements,
    failedElements
  });
  useEffect(() => {
    manualTaskDataEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stableElements = useDeepCompare(elements);
  const memoizedElements = useMemo(() => (stableElements == null ? void 0 : stableElements.map(el => ({
    breakpointId: undefined,
    id: el.id,
    status: convertBooleanStatusToElementStatus(el.isFixed ?? false)
  }))) ?? [], [stableElements]);
  const openInaccessibleLayoutElement = useCallback(_ref2 => {
    let {
      id: elementId
    } = _ref2;
    if (failedElements.length === 0) {
      return;
    }
    const selectedElementIndex = memoizedElements.findIndex(el => el.id === elementId);
    setCurrentElementInfo({
      elementIndex: selectedElementIndex,
      elementStatus: convertElementStatusToBooleanStatus(memoizedElements[selectedElementIndex].status)
    });
    const nextCompRef = {
      id: elementId,
      type: getCompRefType(editorHost)
    };
    if (nextCompRef) {
      a11yWizardAdapter.selectComponent(nextCompRef);
    }
  }, [failedElements.length, memoizedElements, setCurrentElementInfo, editorHost, a11yWizardAdapter]);
  useEffect(() => {
    openNextElement(memoizedElements, openInaccessibleLayoutElement, () => {});
  }, [memoizedElements, openInaccessibleLayoutElement, openNextElement]);

  // If no failed elements, don't render anything (but session sync still happens above)
  if (failedElements.length === 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "9px"
  }, elements.map((element, index) => {
    const {
      id
    } = element;
    const onOpenElement = () => {
      const compRef = {
        id,
        type: getCompRefType(editorHost)
      };
      a11yWizardAdapter.selectComponent(compRef);
    };
    return /*#__PURE__*/React.createElement(InaccessibleLayoutRule, {
      contentRef: selectedItemContentRef,
      element: element,
      elementIndex: index,
      headerRef: selectedItemHeaderRef,
      isOpen: selectedElementId === id,
      key: id,
      pageId: pageId,
      toggleElement: toggleElement(id, onOpenElement)
    });
  })));
};
//# sourceMappingURL=inaccessible-layout-rule-fixer.js.map